import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import type { DragEndEvent } from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { formatDuration, videoWatchUrl } from "../../lib/youtube";

/**
 * Drag-reorderable item list for the series editor.
 *
 * Items are pre-fetched by the parent. Drag end produces an optimistic
 * reorder via the `onReorder` callback (parent applies the change to its
 * local state and calls the server `position` PATCH per moved item).
 *
 * Key + accessibility: keyboard sensor enables ↑/↓ + space-to-grab. The
 * activator is just the drag handle (the ≡ glyph), so clicks elsewhere
 * on the row don't accidentally start a drag.
 */

export interface SortableItem {
  id: string;        // playlistItem id
  videoId: string;
  title: string;
  thumbnailUrl: string;
  durationSec: number | null;
}

interface Props {
  items: SortableItem[];
  onReorder: (next: SortableItem[]) => void;
  onRemove: (itemId: string) => void;
  busyItemId?: string | null;
}

export function SortableVideoList({
  items,
  onReorder,
  onRemove,
  busyItemId,
}: Props) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const oldIndex = items.findIndex((it) => it.id === active.id);
    const newIndex = items.findIndex((it) => it.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    onReorder(arrayMove(items, oldIndex, newIndex));
  };

  if (items.length === 0) {
    return (
      <p className="yt-admin-empty">
        No videos in this series yet — add the first one below.
      </p>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={items.map((it) => it.id)}
        strategy={verticalListSortingStrategy}
      >
        <ul className="yt-sort-list">
          {items.map((it, i) => (
            <SortableRow
              key={it.id}
              item={it}
              position={i + 1}
              onRemove={() => onRemove(it.id)}
              busy={busyItemId === it.id}
            />
          ))}
        </ul>
      </SortableContext>
    </DndContext>
  );
}

function SortableRow({
  item,
  position,
  onRemove,
  busy,
}: {
  item: SortableItem;
  position: number;
  onRemove: () => void;
  busy: boolean;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <li ref={setNodeRef} style={style} className="yt-sort-row">
      <button
        type="button"
        ref={setActivatorNodeRef}
        className="yt-sort-handle"
        aria-label="Drag to reorder"
        {...attributes}
        {...listeners}
      >
        <span aria-hidden>≡</span>
      </button>
      <span className="yt-sort-pos tabular">
        {String(position).padStart(2, "0")}
      </span>
      <img
        src={item.thumbnailUrl}
        alt=""
        loading="lazy"
        className="yt-sort-thumb"
      />
      <div className="yt-sort-body min-w-0">
        <a
          href={videoWatchUrl(item.videoId)}
          target="_blank"
          rel="noopener noreferrer"
          className="yt-sort-title"
        >
          {item.title}
        </a>
        <p className="yt-sort-meta tabular">
          {formatDuration(item.durationSec)}
        </p>
      </div>
      <button
        type="button"
        className="row-action is-danger"
        onClick={onRemove}
        disabled={busy}
      >
        {busy ? "Removing…" : "Remove"}
      </button>
    </li>
  );
}
